/**
 * SPEKTRX Decision Loop — capture layer v0.1 (2026-07-10)
 *
 * Append-only ledger of advisory recommendation lifecycle events:
 *   rec_emitted | rec_acted | rec_dismissed | rec_expired | rec_superseded
 *
 * Design constraints (non-negotiable):
 * - Fully OUTSIDE the S state blob: own IndexedDB database (`zagx_ledger`)
 *   and own Supabase table (`advisory_ledger`, insert-only via PostgREST).
 *   cloudSave payload size is untouched.
 * - Never breaks the advisory flow: every public emit function is
 *   fire-and-forget and swallows its own errors.
 * - Bounded network cost: one batched INSERT at most once per FLUSH_MS,
 *   nothing sent when the queue is empty, own circuit breaker.
 * - Multi-device safe: event `id` is the primary key; inserts use
 *   `Prefer: resolution=ignore-duplicates` so replays are harmless.
 *
 * State access follows the project convention: no `import { S }` here —
 * the reference is injected once from main.js via _setLedgerStateRef().
 */

import { uid } from './utils.js';
import { _sbReady, _isOnline, _sbHeaders, SB_REST } from './auth.js';
import { CircuitBreaker } from './resilience.js';

// ─── CONSTANTS ──────────────────────────────────────────────────
export const LEDGER_IDB = 'zagx_ledger';
export const LEDGER_STORE = 'events';
const FLUSH_MS = 60000;        // min interval between cloud flushes
const FLUSH_BATCH = 200;       // max rows per INSERT
const PRUNE_DAYS = 90;         // synced rows older than this are pruned locally

// ─── STATE ──────────────────────────────────────────────────────
let _S = null;                 // injected state ref (read-only use: prices, fxRate)
let _db = null;                // pooled IDB connection
let _flushTimer = null;
let _flushing = false;

export function _setLedgerStateRef(s) { _S = s; }

const breaker = new CircuitBreaker('AdvisoryLedger', {
  failThreshold: 3,
  cooldownMs: 300000,          // 5 min, same posture as the Supabase state breaker
  successThreshold: 1,
});

// ─── PURE BUILDERS (exported for tests) ─────────────────────────

/** Live price for a ticker from injected state, or null. */
function _priceOf(ticker) {
  if (!ticker || !_S || !_S.prices) return null;
  const p = _S.prices[ticker];
  return p && Number.isFinite(p.price) ? p.price : null;
}

/**
 * Build one ledger row (snake_case keys = Supabase columns).
 * Full payload (metrics/reasoning) only on rec_emitted; lifecycle events
 * stay small — ids, links, and price/fx at event time.
 */
export function buildLedgerRow(event, rec, extra = {}) {
  const m = rec.metrics || null;
  const emitted = event === 'rec_emitted';
  return {
    id: uid(),
    ts: new Date().toISOString(),
    event,
    rec_id: rec.id,
    scan_id: rec.scanId || null,
    rec_type: rec.type || null,
    ticker: rec.ticker || null,
    urgency: rec.urgency || null,
    engine_score: Number.isFinite(rec.engineScore) ? rec.engineScore : null,
    confidence: m && Number.isFinite(m.confidence) ? m.confidence : null,
    price_at: extra.price_at !== undefined ? extra.price_at : _priceOf(rec.ticker),
    fx_at: _S && Number.isFinite(_S.fxRate) ? _S.fxRate : null,
    metrics: emitted ? m : null,
    // lifecycle rows: reasoning column carries the dismiss reason enum (later/disagree/
    // mandate_mismatch) — reuses the existing column, no schema change (zero-bloat rule)
    reasoning: emitted ? (rec.reasoning || null) : (extra.reason || null),
    impact_news_ids: emitted && m && Array.isArray(m.newsIds) && m.newsIds.length ? m.newsIds : null,
    trade_id: extra.trade_id || null,
    cash_tx_id: extra.cash_tx_id || null,
    superseded_by: extra.superseded_by || null,
  };
}

/**
 * Diff a recommendation merge: which previous recs silently disappeared,
 * and were they replaced (superseded) or did their trigger die (expired)?
 * Acted/dismissed recs are skipped — their lifecycle event was already
 * recorded at act/dismiss time.
 * Returns [{ event, rec, extra }].
 */
export function diffRecLifecycle(prevRecs, newRecs, finalRecs) {
  const finalIds = new Set((finalRecs || []).map(r => r.id));
  const newByKey = new Map((newRecs || []).map(r => [`${r.ticker}:${r.type}`, r]));
  const out = [];
  for (const p of prevRecs || []) {
    if (p.dismissed || p.acted) continue;
    if (finalIds.has(p.id)) continue;
    const successor = newByKey.get(`${p.ticker}:${p.type}`);
    out.push(
      successor
        ? { event: 'rec_superseded', rec: p, extra: { superseded_by: successor.id } }
        : { event: 'rec_expired', rec: p, extra: {} }
    );
  }
  return out;
}

// ─── IDB (own database — never shared with zagx_backups) ────────

function _open() {
  if (_db) {
    try { _db.objectStoreNames; return Promise.resolve(_db); }
    catch (_e) { _db = null; }
  }
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(LEDGER_IDB, 1);
    req.onupgradeneeded = e => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(LEDGER_STORE)) {
        const st = db.createObjectStore(LEDGER_STORE, { keyPath: 'id' });
        st.createIndex('synced', 'synced');
      }
    };
    req.onsuccess = e => {
      _db = e.target.result;
      _db.onclose = () => { _db = null; };
      _db.onversionchange = () => { _db.close(); _db = null; };
      resolve(_db);
    };
    req.onerror = e => reject(e.target.error);
  });
}

async function _idbPut(rows) {
  const db = await _open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(LEDGER_STORE, 'readwrite');
    const st = tx.objectStore(LEDGER_STORE);
    rows.forEach(r => st.put(r));
    tx.oncomplete = () => resolve();
    tx.onerror = e => reject(e.target.error);
  });
}

async function _idbUnsynced(limit) {
  const db = await _open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(LEDGER_STORE, 'readonly');
    const idx = tx.objectStore(LEDGER_STORE).index('synced');
    const out = [];
    const cur = idx.openCursor(IDBKeyRange.only(0));
    cur.onsuccess = e => {
      const c = e.target.result;
      if (c && out.length < limit) { out.push(c.value); c.continue(); }
      else resolve(out);
    };
    cur.onerror = e => reject(e.target.error);
  });
}

async function _idbMarkSynced(ids) {
  const db = await _open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(LEDGER_STORE, 'readwrite');
    const st = tx.objectStore(LEDGER_STORE);
    ids.forEach(id => {
      const g = st.get(id);
      g.onsuccess = () => {
        if (g.result) st.put({ ...g.result, synced: 1 });
      };
    });
    tx.oncomplete = () => resolve();
    tx.onerror = e => reject(e.target.error);
  });
}

/** Prune synced rows older than PRUNE_DAYS (cloud copy is source of truth). */
async function _idbPrune() {
  const cutoff = Date.now() - PRUNE_DAYS * 86400000;
  const db = await _open();
  return new Promise(resolve => {
    const tx = db.transaction(LEDGER_STORE, 'readwrite');
    const idx = tx.objectStore(LEDGER_STORE).index('synced');
    const cur = idx.openCursor(IDBKeyRange.only(1));
    cur.onsuccess = e => {
      const c = e.target.result;
      if (c) {
        if (Date.parse(c.value.ts) < cutoff) c.delete();
        c.continue();
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve(); // prune is best-effort
  });
}

// ─── QUEUE + FLUSH ──────────────────────────────────────────────

function _queue(rows) {
  if (!rows || !rows.length) return;
  _idbPut(rows.map(r => ({ ...r, synced: 0 })))
    .then(() => _scheduleFlush())
    .catch(e => console.warn('[LEDGER] queue failed:', e && e.message));
}

function _scheduleFlush() {
  if (_flushTimer) return; // already pending — bounded to one flush per FLUSH_MS
  _flushTimer = setTimeout(() => { _flushTimer = null; flushLedger(); }, FLUSH_MS);
}

/**
 * Push unsynced events to Supabase. Bounded, breaker-guarded, dedup-safe.
 * No request at all when the local queue is empty.
 */
export async function flushLedger() {
  if (_flushing) return;
  if (!_sbReady || !_isOnline || !breaker.canRequest()) return;
  _flushing = true;
  try {
    const rows = await _idbUnsynced(FLUSH_BATCH);
    if (!rows.length) return;
    const headers = await _sbHeaders();
    if (!headers) return; // not authenticated — retry on a later flush
    const payload = rows.map(({ synced: _s, ...r }) => r);
    const res = await fetch(`${SB_REST}/advisory_ledger`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
        'Prefer': 'resolution=ignore-duplicates,return=minimal',
      },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      await _idbMarkSynced(rows.map(r => r.id));
      breaker.recordSuccess();
      // More waiting? Chain another cycle through the normal debounce.
      if (rows.length === FLUSH_BATCH) _scheduleFlush();
    } else {
      breaker.recordFailure();
      console.warn('[LEDGER] flush rejected:', res.status);
    }
  } catch (e) {
    breaker.recordFailure();
    console.warn('[LEDGER] flush failed:', e && e.message);
  } finally {
    _flushing = false;
  }
}

/**
 * Read the full local ledger (Alpha Ledger scoring input). READ-ONLY —
 * scoring is derived on demand and never stored (Supabase zero-bloat).
 */
export async function ledgerReadAll() {
  try {
    const db = await _open();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(LEDGER_STORE, 'readonly');
      const all = tx.objectStore(LEDGER_STORE).getAll();
      all.onsuccess = () => resolve(all.result || []);
      all.onerror = e => reject(e.target.error);
    });
  } catch (e) {
    console.warn('[LEDGER] readAll failed:', e && e.message);
    return [];
  }
}

// ─── PUBLIC EMIT API (fire-and-forget, never throws) ────────────

/** Record freshly emitted recommendations (captures price/fx at emission). */
export function ledgerRecEmitted(recs) {
  try {
    _queue((recs || []).map(r => buildLedgerRow('rec_emitted', r)));
  } catch (e) { console.warn('[LEDGER] emit failed:', e && e.message); }
}

/** Record a rec the user acted on; link the resulting trade when known. */
export function ledgerRecActed(rec, extra = {}) {
  try {
    if (rec) _queue([buildLedgerRow('rec_acted', rec, extra)]);
  } catch (e) { console.warn('[LEDGER] emit failed:', e && e.message); }
}

/** Record an explicit dismissal. */
export function ledgerRecDismissed(rec, reason = 'later') {
  try {
    if (rec) _queue([buildLedgerRow('rec_dismissed', rec, { reason })]);
  } catch (e) { console.warn('[LEDGER] emit failed:', e && e.message); }
}

/**
 * Record recs that silently vanished in a scan merge (superseded/expired).
 * Call AFTER the merge with: previous array, freshly stamped recs, final array.
 */
export function ledgerMergeDiff(prevRecs, newRecs, finalRecs) {
  try {
    const events = diffRecLifecycle(prevRecs, newRecs, finalRecs);
    _queue(events.map(ev => buildLedgerRow(ev.event, ev.rec, ev.extra)));
  } catch (e) { console.warn('[LEDGER] emit failed:', e && e.message); }
}

// ─── INIT ───────────────────────────────────────────────────────

/** Boot: prune old synced rows, flush any backlog, keep flushing on interval. */
export function initDecisionLedger() {
  try {
    _idbPrune().catch(() => {});
    setTimeout(() => flushLedger(), 15000);       // first flush after boot settles
    setInterval(() => flushLedger(), FLUSH_MS * 5); // safety net if debounce chain dies
    if (typeof window !== 'undefined') {
      window._advisoryLedger = { flush: flushLedger, breaker, unsynced: () => _idbUnsynced(1000) };
    }
  } catch (e) { console.warn('[LEDGER] init failed:', e && e.message); }
}
